import { useMemo } from 'react';
import {
  FIELD_TYPES,
  type Column,
  type TableFormSchemaConfig,
} from '@tw/tw-component-library';
import { FormSchemaConfig } from '../../types/form/schema.types';
import { FormState } from '../../types/core/form-state.types';
import { DisabledStateManager } from '../types';
import { validateRequired, validateTagPicker } from '../Utils';
import {
  EMPTY_SELECT_OPTIONS,
  OVERTIME_FIELD_LABELS,
  OVERTIME_GRID_COLUMN_LABELS,
  OVERTIME_GROUP_TITLES,
  OVERTIME_POLICY_TEXT,
  OVERTIME_SECTION_KEYS,
  OVERTIME_SECTION_TITLES,
} from './constants';
import { OvertimeWorkedColumn } from './types';
import {
  OVERTIME_CLIENT_MATTER_ROW_FIELDS,
  OVERTIME_WORKED_GRID_KEY,
  extractOvertimeWorkedRows,
  getOvertimeClientMatterFieldExtras,
  hasOvertimeMatterInGrid,
  validateOvertimeWorkedGrid,
} from './utils';
import { CustomTextDisplay } from '../../../../common/CustomTextDisplay';
import { UseAuth } from '../../../../../contexts/AuthContext';
import {
  getSpoofUserHeader,
  isManager,
} from '../../../../../utils/permissions';

export const useOvertimeApprovalRequestDetailsSchema = (
  formState: FormState
): FormSchemaConfig => {
  const { currentUser, spoofedUser } = UseAuth();

  let isEditAllowed = false;
  if (
    Array.isArray(formState.data?.userAssignedSections) &&
    (formState.data?.userAssignedSections as string[]).includes(
      OVERTIME_SECTION_KEYS.requestDetails
    )
  ) {
    isEditAllowed = true;
  } else {
    isEditAllowed = isManager(
      formState.data?.line_manager,
      currentUser,
      getSpoofUserHeader(spoofedUser)
    );
  }

  const disabledState = useMemo<DisabledStateManager>(() => {
    const isFormCompleted =
      formState.completedSections?.includes(
        OVERTIME_SECTION_KEYS.requestDetails
      ) || false;
    const isFormRejected = formState.isRejected || false;

    return {
      isFormCompleted,
      isFieldDisabled: (
        fieldType: 'readonly' | 'editable' | 'conditional' = 'editable'
      ) => {
        switch (fieldType) {
          case 'readonly':
            return true;
          case 'conditional':
          case 'editable':
          default:
            return (isFormCompleted && !isFormRejected) || !isEditAllowed;
        }
      },
      isTableDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isPaymentTotalDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isTagPickerDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isFileUploadDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isTextAreaDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isRadioDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isSelectDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
    };
  }, [formState.completedSections, formState.isRejected, isEditAllowed]);

  const columns = useMemo<OvertimeWorkedColumn[]>(() => {
    const allColumns: OvertimeWorkedColumn[] = [
      {
        key: 'date',
        header: OVERTIME_GRID_COLUMN_LABELS.date,
        sortable: true,
        type: FIELD_TYPES.Date,
        required: true,
      },
      {
        key: 'day',
        header: OVERTIME_GRID_COLUMN_LABELS.day,
        sortable: true,
        type: FIELD_TYPES.Text,
        required: false,
        fieldConfig: {
          disabled: true,
        },
      },
      {
        key: 'from_time',
        header: OVERTIME_GRID_COLUMN_LABELS.fromTime,
        sortable: true,
        type: 'time',
        required: true,
        extras: {
          timePicker: {
            variant: 'popup',
          },
        },
      },
      {
        key: 'to_time',
        header: OVERTIME_GRID_COLUMN_LABELS.toTime,
        sortable: true,
        type: 'time',
        required: true,
        extras: {
          timePicker: {
            variant: 'popup',
          },
        },
      },
      {
        key: 'hours',
        header: OVERTIME_GRID_COLUMN_LABELS.hours,
        sortable: true,
        type: FIELD_TYPES.Number,
        required: false,
        fieldConfig: {
          disabled: true,
        },
      },
      {
        key: 'matter_number',
        header: OVERTIME_GRID_COLUMN_LABELS.matterNumber,
        sortable: true,
        type: FIELD_TYPES.Text,
        required: false,
      },
      {
        key: 'reason',
        header: OVERTIME_GRID_COLUMN_LABELS.reason,
        sortable: true,
        type: FIELD_TYPES.Text,
        required: true,
      },
    ];

    if (
      !hasOvertimeMatterInGrid(formState.data?.[OVERTIME_WORKED_GRID_KEY])
    ) {
      return allColumns.filter((column) => column.key !== 'matter_number');
    }

    return allColumns;
  }, [formState.data]);

  return useMemo<FormSchemaConfig>(
    () => ({
      sections: [
        {
          isGrouped: true,
          isGroupedAccordion: true,
          sections: [
            {
              title: { text: OVERTIME_GROUP_TITLES.employeeDetails },
              layout: 'auto',
              fields: [
                {
                  type: 'text',
                  name: 'employee_name',
                  label: OVERTIME_FIELD_LABELS.employeeName,
                  required: true,
                  validate: (value: unknown) =>
                    validateRequired(value, OVERTIME_FIELD_LABELS.employeeName),
                  disabled: disabledState.isFieldDisabled('readonly'),
                },
                {
                  type: 'text',
                  name: 'job_title',
                  label: OVERTIME_FIELD_LABELS.jobTitle,
                  required: true,
                  validate: (value: unknown) =>
                    validateRequired(value, OVERTIME_FIELD_LABELS.jobTitle),
                  disabled: disabledState.isFieldDisabled('readonly'),
                },
                {
                  type: 'text',
                  name: 'department',
                  label: OVERTIME_FIELD_LABELS.department,
                  required: true,
                  validate: (value: unknown) =>
                    validateRequired(value, OVERTIME_FIELD_LABELS.department),
                  disabled: disabledState.isFieldDisabled('readonly'),
                },
                {
                  type: 'text',
                  name: 'employee_number',
                  label: OVERTIME_FIELD_LABELS.employeeNumber,
                  required: true,
                  validate: (value: unknown) =>
                    validateRequired(
                      value,
                      OVERTIME_FIELD_LABELS.employeeNumber,
                    ),
                  disabled: disabledState.isFieldDisabled('readonly'),
                },
                {
                  type: 'text',
                  name: 'role_type',
                  label: OVERTIME_FIELD_LABELS.roleType,
                  required: true,
                  options: EMPTY_SELECT_OPTIONS,
                  validate: (value: unknown) =>
                    validateRequired(value, OVERTIME_FIELD_LABELS.roleType),
                  disabled: disabledState.isFieldDisabled('readonly'),
                },
                {
                  type: 'text',
                  name: 'standard_working_hours',
                  label: OVERTIME_FIELD_LABELS.standardWorkingHours,
                  required: true,
                  placeholder: 'Please enter your working hours',
                  validate: (value: unknown) =>
                    validateRequired(
                      value,
                      OVERTIME_FIELD_LABELS.standardWorkingHours,
                    ),
                  disabled: disabledState.isFieldDisabled('conditional'),
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: true,
              },
            },
            {
              title: { text: OVERTIME_SECTION_TITLES.requestDetails },
              layout: 'single',
              fields: [
                {
                  type: 'customComponent',
                  name: 'overtime_policy_display',
                  label: OVERTIME_FIELD_LABELS.overtimePolicy,
                  customComponent: (
                    <CustomTextDisplay
                      text={OVERTIME_POLICY_TEXT}
                      size="medium"
                    />
                  ),
                },
                {
                  type: 'tagPicker',
                  name: 'line_manager',
                  label: OVERTIME_FIELD_LABELS.lineManager,
                  required: true,
                  validate: (value: unknown) =>
                    validateTagPicker(value, OVERTIME_FIELD_LABELS.lineManager),
                  disabled: disabledState.isTagPickerDisabled(),
                  extras: {
                    tagPicker: {
                      minSearchLength: 4,
                      maxTags: 1,
                      createNewTags: false,
                    },
                  },
                },
                {
                  type: FIELD_TYPES.Table,
                  name: OVERTIME_WORKED_GRID_KEY,
                  label: OVERTIME_FIELD_LABELS.overtimeWorked,
                  required: true,
                  disabled: disabledState.isTableDisabled(),
                  validate: (value: unknown) =>
                    validateOvertimeWorkedGrid(value),
                  extras: {
                    table: {
                      data: extractOvertimeWorkedRows(
                        formState.data?.[OVERTIME_WORKED_GRID_KEY],
                      ),
                      columns: columns as unknown as Column<
                        Record<string, unknown>
                      >[],
                      enableSort: true,
                      enableAddRow: true,
                      enableEdit: true,
                      enableDelete: true,
                      enableSearch: false,
                      enableFilters: false,
                      showRowNumbers: true,
                      disabled: disabledState.isTableDisabled(),
                      tableFormSchema: {
                        useFormSchema: true,
                        formSchema: {
                          sections: [
                            {
                              title: {
                                text: OVERTIME_FIELD_LABELS.overtimeWorked,
                                type: 'h2',
                              },
                              fields: [
                                {
                                  name: 'date',
                                  label: OVERTIME_GRID_COLUMN_LABELS.date,
                                  type: FIELD_TYPES.Date,
                                  required: true,
                                  validate: (value: unknown) =>
                                    validateRequired(
                                      value,
                                      OVERTIME_GRID_COLUMN_LABELS.date,
                                    ),
                                },
                                {
                                  name: 'day',
                                  label: OVERTIME_GRID_COLUMN_LABELS.day,
                                  type: FIELD_TYPES.Text,
                                  placeholder: 'Auto-generated',
                                  required: false,
                                  disabled: true,
                                },
                                {
                                  name: 'from_time',
                                  label: OVERTIME_GRID_COLUMN_LABELS.fromTime,
                                  type: 'time',
                                  required: true,
                                  validate: (value: unknown) =>
                                    validateRequired(
                                      value,
                                      OVERTIME_GRID_COLUMN_LABELS.fromTime,
                                    ),
                                  extras: {
                                    timePicker: {
                                      variant: 'popup',
                                    },
                                  },
                                },
                                {
                                  name: 'to_time',
                                  label: OVERTIME_GRID_COLUMN_LABELS.toTime,
                                  type: 'time',
                                  required: true,
                                  validate: (value: unknown) =>
                                    validateRequired(
                                      value,
                                      OVERTIME_GRID_COLUMN_LABELS.toTime,
                                    ),
                                  extras: {
                                    timePicker: {
                                      variant: 'popup',
                                    },
                                  },
                                },
                                {
                                  name: 'hours',
                                  label: OVERTIME_GRID_COLUMN_LABELS.hours,
                                  type: FIELD_TYPES.Number,
                                  placeholder: 'Auto-generated',
                                  required: false,
                                  disabled: true,
                                },
                                {
                                  name: OVERTIME_CLIENT_MATTER_ROW_FIELDS.picker,
                                  label: OVERTIME_FIELD_LABELS.matter,
                                  type: FIELD_TYPES.ClientMatter,
                                  required: false,
                                  extras: {
                                    clientMatter:
                                      getOvertimeClientMatterFieldExtras(
                                        formState.office,
                                      ),
                                  },
                                },
                                {
                                  name: OVERTIME_CLIENT_MATTER_ROW_FIELDS.matterNumber,
                                  label: OVERTIME_FIELD_LABELS.matterNumber,
                                  type: FIELD_TYPES.Text,
                                  placeholder: 'Auto-generated',
                                  required: false,
                                  disabled: true,
                                  dependencies: [
                                    {
                                      type: 'show',
                                      logicalOperator: 'AND',
                                      conditions: [
                                        {
                                          field:
                                            OVERTIME_CLIENT_MATTER_ROW_FIELDS.matterNumber,
                                          operator: 'isNotEmpty',
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  name: 'reason',
                                  label: OVERTIME_GRID_COLUMN_LABELS.reason,
                                  type: FIELD_TYPES.Text,
                                  required: true,
                                  validate: (value: unknown) =>
                                    validateRequired(
                                      value,
                                      OVERTIME_GRID_COLUMN_LABELS.reason,
                                    ),
                                },
                              ],
                            },
                          ],
                        },
                      } as unknown as TableFormSchemaConfig,
                    },
                  },
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: true,
              },
            },
          ],
          extras: { grouped: true, evenSpacing: true },
        },
      ],
    }),
    [columns, disabledState, formState.data, formState.office],
  );
};
